import useFetch from '@e3dc-react/shell/Hooks/useFetch'
import Joi from 'joi'

import React, { useEffect, useState } from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { PvModuleModel } from './PvModuleModels'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useValidation } from '@e3dc-react/shell/Hooks/useValidation/useValidation'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useDispatch } from 'react-redux'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import { Grid } from '@mui/material'
import { TextField, NumberField } from '@e3dc-react/shell/Elements/InputFields'
import { LineChartSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { UnitTypes } from 'src/Libs/types'

const stateSchema = Joi.object().keys({
  ID: Joi.number(),
  PRODUCER: Joi.string(),
  MODUL: Joi.string(),
  V1: Joi.number(),
  V2: Joi.number(),
  V3: Joi.number(),
  V4: Joi.number(),
  V5: Joi.number(),
})

export interface PvModuleEditorProps {
  onClose: () => void
  place: number
  pvModuleId?: number
}

const defaultState: Partial<PvModuleModel> = {
  PRODUCER: '',
  MODUL: '',
  V1: 0,
  V2: 0,
  V3: 0,
  V4: 0,
  V5: 0,
}

const PvModuleEditor: React.VFC<PvModuleEditorProps> = ({ pvModuleId, place, onClose }) => {
  const authFetch = useAuthFetchWithResult()
  const { t } = useCustomTranslation()
  const [pvModule, pvModuleLoading] = useFetch<PvModuleModel>({
    url: `pv-modules/${pvModuleId}`,
    doNotFetch: pvModuleId === undefined,
  })
  const [saving, setSaving] = useState(false)
  const [currentPvModule, setCurrentPvModule] = useState<Partial<PvModuleModel>>(defaultState)
  const [validationActive, setValidationActive] = useState(false)
  const validationObject = useValidation(currentPvModule, stateSchema)
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const dispatch = useDispatch()

  useEffect(() => {
    if (pvModule) setCurrentPvModule(pvModule)
  }, [pvModule])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleInput = (name: string, value: any): void => {
    setCurrentPvModule({ ...currentPvModule, [name]: value })
  }

  const savePvModule = async (): Promise<void> => {
    // if save button clicked for the first time activate the validation
    if (!validationActive) setValidationActive(true)
    // if validation fails do not save
    if (Object.keys(validationObject).length > 0) return

    const { ID, ...tmpPvModule } = currentPvModule

    if (pvModuleId) {
      try {
        setSaving(true)
        await authFetch({
          path: `pv-modules/${pvModuleId}`,
          method: 'PUT',
          payload: tmpPvModule,
        })
        enqueueSuccessStack(t('common.putSuccess', { item: t('common.pvModule') }))
      } catch (error) {
        const err = error as AuthFetchError

        enqueueErrorStack(t('common.putFailed', { item: t('common.pvModule') }), err.code, err.message)
      } finally {
        setSaving(false)
      }
    } else {
      try {
        setSaving(true)
        await authFetch({
          path: 'pv-modules',
          method: 'POST',
          payload: tmpPvModule,
        })
        enqueueSuccessStack(t('common.postSuccess', { item: t('common.pvModule') }))
      } catch (error) {
        const err = error as AuthFetchError

        enqueueErrorStack(t('common.postFailed', { item: t('common.pvModule') }), err.code, err.message)
      } finally {
        setSaving(false)
      }
    }
    dispatch(refreshApiView())
    onClose()
  }

  const validation = validationActive ? validationObject : {}

  return (
    <Dialog
      title={pvModuleId ? t('pvModules.edit') : t('pvModules.add')}
      onClose={() => onClose()}
      place={place}
      actions={{
        primary: {
          name: t('pvModules.save'),
          disabled: (validationActive && Object.keys(validationObject).length > 0) || !currentPvModule || pvModuleLoading,
          onClick: () => savePvModule(),
          isLoading: saving,
        },
        secondary: [
          {
            name: t('common.cancel'),
          },
        ],
      }}
    >
      {pvModuleLoading ? (
        <LineChartSkeleton />
      ) : (
        <Grid container alignItems="center" spacing={2} justifyContent="center">
          <Grid item xs={12} md={6}>
            <TextField
              helperText={validation['PRODUCER'] ? validation['PRODUCER'].message : undefined}
              label={t('pvModules.attributes.producer')}
              value={currentPvModule.PRODUCER || ''}
              onChange={(value: string) => handleInput('PRODUCER', value)}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              helperText={validation['MODUL'] ? validation['MODUL'].message : undefined}
              label={t('pvModules.attributes.modul')}
              value={currentPvModule.MODUL || ''}
              onChange={(value: string) => handleInput('MODUL', value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <NumberField
              helperText={validation['V1'] ? validation['V1'].message : undefined}
              label={t('pvModules.attributes.v1')}
              value={currentPvModule.V1 || undefined}
              onChange={value => handleInput('V1', value)}
              suffix={UnitTypes.watt}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <NumberField
              helperText={validation['V2'] ? validation['V2'].message : undefined}
              label={t('pvModules.attributes.v2')}
              value={currentPvModule.V2 || undefined}
              onChange={value => handleInput('V2', value)}
              suffix={UnitTypes.ampere}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <NumberField
              helperText={validation['V3'] ? validation['V3'].message : undefined}
              label={t('pvModules.attributes.v3')}
              value={currentPvModule.V3 || undefined}
              onChange={value => handleInput('V3', value)}
              suffix={UnitTypes.volt}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <NumberField
              helperText={validation['V4'] ? validation['V4'].message : undefined}
              label={t('pvModules.attributes.v4')}
              value={currentPvModule.V4 || undefined}
              onChange={value => handleInput('V4', value)}
              suffix={UnitTypes.volt}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <NumberField
              helperText={validation['V5'] ? validation['V5'].message : undefined}
              label={t('pvModules.attributes.v5')}
              value={currentPvModule.V5 || undefined}
              onChange={value => handleInput('V5', value)}
              suffix="% / K"
            />
          </Grid>
        </Grid>
      )}
    </Dialog>
  )
}

export default PvModuleEditor
